import { AuthContext } from '@modules/auth/contexts/auth-plus.context';
import { useContext, useState } from 'react';
import { FaDatabase, FaTrashAlt, FaSyncAlt, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { SystemSettingService } from '../services/system-setting.service';

interface Section {
  title: string;
  key: string;
  subtitle?: string;
  removeDupeAction: () => Promise<boolean>;
  syncIndexAction: () => Promise<boolean>;
}

function MongoDBSetting() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Section configuration with their corresponding API actions
  const sections: Section[] = [
    {
      title: 'Follow',
      key: 'follow',
      removeDupeAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.removeFollowDuplicate(auth, setAuth, logout);
      },
      syncIndexAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.syncFollowIndexes(auth, setAuth, logout);
      }
    },
    {
      title: 'Connect',
      subtitle: 'Connect and Partner',
      key: 'connect',
      removeDupeAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.removeDupeConnect(auth, setAuth, logout);
      },
      syncIndexAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.syncConnectIndexes(auth, setAuth, logout);
      }
    },
    {
      title: 'Social Reactions',
      key: 'social',
      removeDupeAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.removeDupeSocialReaction(auth, setAuth, logout);
      },
      syncIndexAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.syncSocialReactionIndexes(auth, setAuth, logout);
      }
    },
    {
      title: 'Community (Fitty) Reactions',
      key: 'community',
      removeDupeAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.removeDupeCommunityReaction(auth, setAuth, logout);
      },
      syncIndexAction: async () => {
        if (!auth) return false;
        return await SystemSettingService.syncIndexesCommunityReactions(auth, setAuth, logout);
      }
    }
  ];

  const handleRemoveDuplicates = async (section: Section) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete duplicate ${section.title} records. This action cannot be undone. Are you sure?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await section.removeDupeAction();

      if (success) {
        alert(`✅ Successfully removed duplicate ${section.title} records!`);
      } else {
        throw new Error('Operation returned false');
      }
    } catch (err: any) {
      setError(err.message || `Failed to remove duplicate ${section.title}`);
      alert(`❌ Failed to remove duplicate ${section.title}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncIndexes = async (section: Section) => {
    if (!window.confirm(`Sync ${section.title} indexes? Make sure you've removed duplicates first.`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await section.syncIndexAction();

      if (success) {
        alert(`✅ Successfully synced ${section.title} indexes!`);
      } else {
        throw new Error('Operation returned false');
      }
    } catch (err: any) {
      setError(err.message || `Failed to sync ${section.title} indexes`);
      alert(`❌ Failed to sync ${section.title} indexes. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="d-flex align-items-center gap-3 mb-4">
              <FaDatabase size={40} className="text-success" />
              <h1 className="display-4 fw-bold text-dark mb-0">MongoDB Management</h1>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>Error:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {/* Loading Overlay */}
            {loading && (
              <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                <div className="text-center">
                  <FaSpinner className="text-white fa-spin" size={50} />
                  <p className="text-white mt-3 fs-5">Processing...</p>
                </div>
              </div>
            )}

            {/* Warning Banner */}
            <div className="alert alert-warning border-warning border-2 mb-4" role="alert">
              <div className="d-flex align-items-start gap-3">
                <FaExclamationTriangle size={24} className="mt-1 flex-shrink-0" />
                <div>
                  <h5 className="alert-heading fw-bold mb-2">Important Guidelines</h5>
                  <ul className="mb-0 small">
                    <li><strong>Remove Duplicates</strong> operations will permanently delete data (Hard Delete)</li>
                    <li>Always run <strong>Remove Duplicates</strong> before <strong>Sync Indexes</strong></li>
                    <li>These operations cannot be undone - proceed with caution</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Database Sections */}
            <div className="row g-4">
              {sections.map((section) => (
                <div key={section.key} className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-header text-white py-3"
                      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                      <h5 className="mb-0 fw-bold">
                        {section.title}
                        {section.subtitle && (
                          <span className="fs-6 fw-normal ms-2">({section.subtitle})</span>
                        )}
                      </h5>
                    </div>
                    <div className="card-body p-4">
                      <div className="row g-3">
                        {/* Remove Duplicates */}
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100 bg-light">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <FaTrashAlt className="text-danger" />
                              <h6 className="mb-0 fw-semibold">Remove Duplications</h6>
                            </div>
                            <p className="text-muted small mb-3">
                              <span className="badge bg-danger me-2">Hard Delete</span>
                              Permanently removes duplicate records
                            </p>
                            <button
                              onClick={() => handleRemoveDuplicates(section)}
                              className="btn btn-danger w-100"
                              disabled={loading}
                            >
                              <FaTrashAlt className="me-2" />
                              Remove Duplicates
                            </button>
                          </div>
                        </div>

                        {/* Sync Indexes */}
                        <div className="col-md-6">
                          <div className="border rounded p-3 h-100 bg-light">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <FaSyncAlt className="text-primary" />
                              <h6 className="mb-0 fw-semibold">Sync Indexes</h6>
                            </div>
                            <p className="text-muted small mb-3">
                              <span className="badge bg-warning text-dark me-2">Prerequisite</span>
                              Run remove duplicates first
                            </p>
                            <button
                              onClick={() => handleSyncIndexes(section)}
                              className="btn btn-primary w-100"
                              disabled={loading}
                            >
                              <FaSyncAlt className="me-2" />
                              Sync Indexes
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Note */}
            <div className="card border-info mt-4">
              <div className="card-body">
                <p className="mb-0 text-muted small">
                  <strong>Note:</strong> All operations require confirmation before execution.
                  If you encounter any issues, please contact the development team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MongoDBSetting;
